import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { Input } from 'ui-components/components/input';

import { CustomerAutoComplete } from '../../components/customer-auto-complete';
import { useCountryAddressFormat } from '../countries/hooks';
import { InputLabel } from '../input-label';
import { messages } from '../messages';
import { PasteAddress } from '../paste-address';
import {
  AddressType,
  CREATE_LABEL_CONTENT_RETURN_FROM_ADDRESS_TYPE,
  SHIP_TO_ADDRESS_TYPE,
  NEW_SHIP_FROM_LOCATION_ADDRESS_TYPE,
} from '../prop-types/address-type';
import style from './style.module.less';
import { PASTE_ADDRESS } from '../constants';
import { emptyArray } from 'ui-components/default-props/empty';

const getNameFieldLabelMessage = (addressType) => {
  if (addressType === CREATE_LABEL_CONTENT_RETURN_FROM_ADDRESS_TYPE) {
    return messages.returnShipFromName;
  }

  if (addressType === NEW_SHIP_FROM_LOCATION_ADDRESS_TYPE) {
    return messages.newShipFromLocationName;
  }

  return messages.name;
};

export const ControlledComponentAddressForm = ({
  addressType = SHIP_TO_ADDRESS_TYPE,
  onChange,
  address,
  email = '',
  hasError,
  hidePasteAddress,
  pasteAddressRequest,
  onBlur,
  autoCompleteRecipientName = false,
  customers = emptyArray,
  onCustomerSelected,
  hiddenFields = emptyArray,
  requiredFields = emptyArray,
  showChevron = true,
  showAddressLine3,
  wrapperClassName = '',
  disabled = false,
  requireAll = false,
  allowEmptyValueToSelect = false,
}) => {
  const addressFormat = useCountryAddressFormat(address.countryCode);
  const intl = useIntl();

  const isFieldRequired = useMemo(() => {
    return (id, required) => {
      if (requiredFields?.length) {
        return requiredFields.includes(id);
      }
      return required || requireAll;
    };
  }, [requiredFields, requireAll]);

  const renderSearchResults = () => {
    if (customers.length > 0) {
      return (
        <div className={style['result-dropdown-menu']}>
          <CustomerAutoComplete customers={customers} onRowClick={(selection) => onCustomerSelected(selection)} path='' />
        </div>
      );
    }
  };

  const renderAutoCompleteCustomerName = ({ componentClassName, labelClassName, id, name, required }) => {
    return (
      <div key={`${id}-input`}>
        <InputLabel className={style[labelClassName]} required={required || requireAll}>
          <FormattedMessage {...messages[id]} />
        </InputLabel>
        <div className={style[componentClassName] || style['full-width-input']}>
          {renderSearchResults()}
          <Input
            data-testid={`${addressFormat.countryCode}-name`}
            hasError={hasError?.name}
            label=''
            name={name}
            onBlur={(value) => onBlur('name', value)}
            onChange={(value) => onChange('name', value)}
            placeholder={intl.formatMessage(getNameFieldLabelMessage(addressType))}
            value={address.name || ''}
          />
        </div>
      </div>
    );
  };

  const renderPasteAddress = (element) => {
    const placeholder = messages[element.placeholderMessageKey] ? intl.formatMessage(messages[element.placeholderMessageKey]) : '';

    if (hidePasteAddress) {
      return null;
    }

    return <PasteAddress key={element.id} pasteAddressRequest={pasteAddressRequest} placeholder={placeholder} showChevron={showChevron} />;
  };

  const renderElement = (element, index) => {
    if (Array.isArray(element)) {
      return (
        <div key={index} className={style['input-row']}>
          {element.map(renderElement)}
        </div>
      );
    }

    if (element.type === PASTE_ADDRESS) {
      return renderPasteAddress(element);
    }

    if (element.id === 'name' && autoCompleteRecipientName) {
      return renderAutoCompleteCustomerName(element);
    }

    if (element.name === 'line3' && !showAddressLine3) {
      return null;
    }

    const { id, labelClassName, required, name, type, renderComponent, placeholderMessageKey, componentClassName } = element;

    const isRequiredField = isFieldRequired(id, required);

    const showLabel = messages[id] ? <FormattedMessage {...messages[id]} /> : null;

    const placeholder = messages[placeholderMessageKey] ? intl.formatMessage(messages[placeholderMessageKey]) : '';

    const errorState = typeof hasError === 'object' ? hasError[name] : hasError;

    const elementProps = {
      name,
      type,
      placeholder,
      componentClassName: style[componentClassName] || style['full-width-input'],
      countryCode: address.countryCode,
      hasError: errorState,
      onBlur: (v) => onBlur(name, v),
      onChange: (v) => onChange(name, v),
      value: email && element.name === 'email' ? email : address[name],
      disabled,
      allowEmptyValueToSelect,
    };

    return (
      <div key={id}>
        {showLabel && (
          <InputLabel
            className={style[labelClassName]}
            disabled={disabled}
            hasError={elementProps.hasError}
            labelFor={id}
            required={isRequiredField}
          >
            {showLabel}
          </InputLabel>
        )}
        {renderComponent(elementProps)}
      </div>
    );
  };

  const { elements } = addressFormat;
  const elementWrapperClassName = wrapperClassName || style['manual-address-wrapper'];
  const inputRows = elements
    .filter((e) => !hiddenFields.includes(e.id))
    .reduce((acc, el) => {
      if (el.isInline) {
        const prevEl = acc[acc.length - 1];
        if (Array.isArray(prevEl)) {
          prevEl.push(el);
        } else {
          acc.push([el]);
        }
      } else {
        acc.push(el);
      }
      return acc;
    }, []);
  return <div className={elementWrapperClassName}>{inputRows.map((row, index) => renderElement(row, index))}</div>;
};

ControlledComponentAddressForm.propTypes = {
  addressType: AddressType,
  onChange: PropTypes.func.isRequired,
  address: PropTypes.shape({
    name: PropTypes.string,
    company: PropTypes.string,
    line1: PropTypes.string,
    line2: PropTypes.string,
    line3: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    countryCode: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  email: PropTypes.string,
  pasteAddressRequest: PropTypes.func.isRequired,
  hasError: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      name: PropTypes.bool,
      company: PropTypes.bool,
      line1: PropTypes.bool,
      line2: PropTypes.bool,
      line3: PropTypes.bool,
      city: PropTypes.bool,
      state: PropTypes.bool,
      postalCode: PropTypes.bool,
      countryCode: PropTypes.bool,
      phone: PropTypes.bool,
      cardName: PropTypes.bool,
      cardProvider: PropTypes.bool,
      cardNumber: PropTypes.bool,
      expiry: PropTypes.bool,
      cvv: PropTypes.bool,
    }),
  ]).isRequired,
  autoCompleteRecipientName: PropTypes.bool,
  customers: PropTypes.array,
  onCustomerSelected: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  hiddenFields: PropTypes.array,
  requiredFields: PropTypes.array,
  showChevron: PropTypes.bool,
  showAddressLine3: PropTypes.bool.isRequired,
  wrapperClassName: PropTypes.string,
  hidePasteAddress: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  requireAll: PropTypes.bool,
  allowEmptyValueToSelect: PropTypes.bool,
};
